import React, { useEffect, useState, useRef } from "react";
import {
    Badge, Card, Flex, Heading, View, Button, Text, Divider, Image
} from "@aws-amplify/ui-react";
import { useNavigate, useParams } from "react-router-dom";
import { get, del } from 'aws-amplify/api';
import PostalMime from 'postal-mime';
import type { Email } from 'postal-mime';
import DOMPurify from 'dompurify';

const escapeHtml = (text: string) =>
    text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

const EmailMessage = () => {
    const [message, setMessage] = useState<Email | null>(null);
    const [summary, setSummary] = useState<string>(null);
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const params = useParams();
    const iframeRef = useRef(null);
    const emailAddress = params.addressId;
    const messageId = params.messageId;
    const navigate = useNavigate();
    const handleBackClick = (emailAddress) => {
        navigate(`/email-accounts/${encodeURIComponent(emailAddress)}`);
    };
    const cleanHTML = (incoming_html) => {
        return DOMPurify.sanitize(incoming_html, {
            FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button'],
            FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onmouseout', 'onfocus', 'onblur', 'onchange', 'onsubmit'],
            ALLOW_DATA_ATTR: false,
            SAFE_FOR_TEMPLATES: true,
            WHOLE_DOCUMENT: false,
            RETURN_DOM: false,
            RETURN_DOM_FRAGMENT: false,
            SANITIZE_DOM: true
        });
    };

    async function handleDeleteMessage(emailAddress, messageId) {
        try {
            setLoading(true)
            const restOperation = del({
                apiName: 'disposible',
                path: `addresses/${emailAddress}/${messageId}`,
            });
            await restOperation.response;
            handleBackClick(emailAddress)
            console.log('DELETE call succeeded');
        } catch (err) {
            console.error('DELETE call failed: ', err);
            setError(err);
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        // The route element stays mounted when only the params change, so a
        // request for the previous message can still be in flight. Cancel it
        // and ignore its result, or the page shows message A's body under
        // message B's header and Delete button.
        let stale = false;
        const restOperation = get({
            apiName: 'disposible',
            path: `addresses/${emailAddress}/${messageId}`,
        });
        setMessage(null);
        setSummary(null);
        setAttachments([]);
        setError(null);
        setLoading(true);
        (async () => {
            try {
                const { body } = await restOperation.response;
                const raw = await body.json();
                const parsed = await new PostalMime().parse(raw["body"]);
                if (stale) return;
                setMessage(parsed);
                setSummary(raw["summary"]);
                setAttachments(raw["attachments"] || []);
            } catch (err) {
                if (stale) return;
                console.error('GET call failed: ', err);
                setError(err);
            } finally {
                if (!stale) setLoading(false);
            }
        })();
        return () => {
            stale = true;
            restOperation.cancel();
        };
    }, [emailAddress, messageId]);

    const resizeIframe = () => {
        const iframe = iframeRef.current;
        // The document body is gone between doc.open()/doc.close(), so guard
        // every dereference.
        const body = iframe?.contentWindow?.document?.body;
        if (!iframe || !body) {
            return;
        }
        iframe.style.width = '100%';
        iframe.style.border = 'none';
        iframe.style.height = '0px';
        iframe.style.height = body.scrollHeight + 'px';
    };

    useEffect(() => {
        if (message && iframeRef.current) {
            // Plain-text mail has no html part. Render the text escaped with
            // whitespace preserved instead of leaving the frame blank.
            const cleanHTMLContent = message.html
                ? cleanHTML(message.html)
                : `<pre style="white-space: pre-wrap; overflow-wrap: anywhere; font-family: ui-monospace, monospace;">${escapeHtml(message.text ?? '')}</pre>`;
            
            // Create a safe HTML document with CSP
            const safeDocument = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="utf-8">
                    <meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data: https:; font-src 'self' data:;">
                    <style>
                        body { font-family: Arial, sans-serif; line-height: 1.6; margin: 20px; }
                        a { color: #0066cc; text-decoration: none; }
                        a:hover { text-decoration: underline; }
                        img { max-width: 100%; height: auto; }
                    </style>
                </head>
                <body>
                    ${cleanHTMLContent}
                </body>
                </html>
            `;
            
            const doc = iframeRef.current.contentDocument;
            doc.open();
            doc.write(safeDocument);
            doc.close();
            
            // Disable JavaScript execution in iframe
            iframeRef.current.contentWindow.eval = function() {
                throw new Error('JavaScript execution is disabled for security');
            };
            
            resizeIframe();
            const iframe = iframeRef.current;
            iframe.addEventListener('load', resizeIframe);
            
            // Add resize event listener
            window.addEventListener('resize', resizeIframe);

            // Cleanup event listener on component unmount
            return () => {
                window.removeEventListener('resize', resizeIframe);
                iframe.removeEventListener('load', resizeIframe);
            };
        }
    }, [message]);

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error fetching message</div>;
    if (!message) return <div>No message data</div>;

    return (
        <>
            <Flex className="header-flex">
                <div className="email-header">
                    <h2>Email Message ID: {messageId}</h2>
                </div>
            </Flex>
            <Flex justifyContent="flex-start" alignItems="center">
                <Button size="small" isLoading={loading} isDisabled={loading} onClick={() => handleDeleteMessage(emailAddress, messageId)} variation="destructive">Delete</Button>
                <div style={{ flex: 1 }}></div> {/* Spacer element */}
                <Button size="small" isLoading={loading} isDisabled={loading} onClick={() => handleBackClick(emailAddress)}>Back</Button>
            </Flex>
            <br></br>
            {summary && (
                <Card style={{
                    width: '100%', // Set width to 100% to fit the screen
                    maxWidth: '100%', // Ensure it doesn't exceed the screen width
                    marginBottom: '1rem',
                    boxSizing: 'border-box', // This ensures padding is included in the width calculation
                }}>
                    <Text textDecoration="underline">Summary</Text>
                    <br></br>
                    <Text>{summary}</Text>
                    <br></br>
                </Card>
            )}
            {attachments.length > 0 && (<Card
                style={{
                    width: '100%', // Set width to 100% to fit the screen
                    maxWidth: '100%', // Ensure it doesn't exceed the screen width
                    marginBottom: '1rem',
                    boxSizing: 'border-box', // This ensures padding is included in the width calculation
                }}
            >
                <Text textDecoration="underline">Attachments</Text>
                <Flex wrap="wrap">
                    {attachments.map((attachment, index) => (
                        <View
                            key={index}
                            style={{
                                margin: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                maxWidth: '150px',
                            }}
                        >
                            {attachment.metadata['Content-Type'].startsWith('image/') ? (
                                <Image
                                    src={attachment.url}
                                    alt={attachment.metadata.filename}
                                    style={{ width: '150px', height: 'auto' }}
                                />
                            ) : (null)}
                            <Text style={{
                                marginTop: '0.5rem',
                                wordWrap: 'break-word', // Ensures text wraps to avoid overflow
                                maxWidth: '150px', // Ensures text does not extend beyond the set width
                            }}>
                                {attachment.metadata.filename}
                            </Text>
                            <Button
                                as="a"
                                href={attachment.url}
                                target="_blank"
                                variation="primary"
                                style={{ marginTop: '0.5rem' }}
                            >
                                Download
                            </Button>
                        </View>
                    ))}
                </Flex>
            </Card>)}
            <View
                backgroundColor="var(--amplify-colors-white)"
                borderRadius="6px"
                maxWidth="100%"
                padding="1rem"
            >
                <Card>
                    <Flex
                        direction="column"
                        alignItems="flex-start">
                        {/* Every header is optional in MIME; a Bcc-only message has no To at all. */}
                        <Badge size="small" variation="info">
                            From: {message.from?.address ?? '(unknown sender)'}
                        </Badge>
                        <Flex direction="row" alignItems="center" wrap="wrap">
                            {(message.to ?? []).map((recipient, index) => (
                                <Badge key={`to-${index}`} size="small" variation="success">
                                    To: {recipient.address}
                                </Badge>
                            ))}
                            {(message.cc ?? []).map((recipient, index) => (
                                <Badge key={`cc-${index}`} size="small" variation="success">
                                    Cc: {recipient.address}
                                </Badge>
                            ))}
                            {!message.to?.length && !message.cc?.length && (
                                <Badge size="small" variation="success">
                                    Delivered to: {emailAddress}
                                </Badge>
                            )}
                        </Flex>
                        <Heading level={5}>
                            Subject: {message.subject ?? '(no subject)'}
                        </Heading>
                        <Divider />
                        {/*
                          Safety here comes from DOMPurify + the meta CSP + this sandbox
                          having NO allow-scripts. allow-same-origin exists only so
                          resizeIframe can read scrollHeight. Never add allow-scripts:
                          combined with allow-same-origin, email content could reach the
                          parent origin (tokens, DOM, everything).
                        */}
                        <iframe
                            ref={iframeRef}
                            title="Email Content"
                            sandbox="allow-same-origin"
                            style={{ width: '100%', border: 'none', minHeight: '400px' }}
                        />
                    </Flex>
                </Card>
            </View>
        </>
    );
};

export default EmailMessage;
